import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Landmark, ShieldCheck, Mail, Phone, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { fiLeadsAPI } from '../../lib/api';

const STATUSES = ['open', 'contacted', 'closed'];

// F&I leads used to leave exactly one trace: an SMS at submission time. Miss
// the text and the lead was invisible forever. This is their queue — the same
// "it survives a missed message" guarantee inquiries and test drives have.
// Presentational: the funnel owns the fetch so the tab badge stays live even
// while this panel is collapsed.
export const CCFILeadsPanel = ({ apps = [], interest = [], failed = false, openCount = 0, onChanged }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState('');

    const setStatus = async (kind, id, status) => {
        setBusy(id);
        try {
            await fiLeadsAPI.setStatus(kind, id, status);
            toast.success(t('fiLeads.saved', 'Lead updated'));
            await onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong')); }
        finally { setBusy(''); }
    };

    const Row = ({ lead, kind, icon: Icon, title, detail }) => (
        <div className="rounded-xl border border-border p-3 space-y-2" data-testid={`fi-lead-${lead.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm font-semibold flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="truncate">{title}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{detail}</p>
                    <p className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                        {lead.email ? <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span> : null}
                        {lead.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span> : null}
                    </p>
                </div>
                <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-full shrink-0 ${lead.lead_status === 'closed' ? 'bg-muted text-muted-foreground' : lead.lead_status === 'contacted' ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400' : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'}`}
                    data-testid={`fi-lead-status-${lead.id}`}>
                    {t(`fiLeads.status.${lead.lead_status || 'open'}`, lead.lead_status || 'open')}
                </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {STATUSES.filter((s) => s !== (lead.lead_status || 'open')).map((s) => (
                    <Button key={s} size="sm" variant="outline" disabled={busy === lead.id}
                        onClick={() => setStatus(kind, lead.id, s)}
                        data-testid={`fi-lead-set-${s}-${lead.id}`}>
                        {t(`fiLeads.mark.${s}`, `Mark ${s}`)}
                    </Button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="space-y-5" data-testid="cc-fi-leads">
            {failed ? (
                <p className="text-sm text-amber-500 flex items-center gap-2" data-testid="cc-fi-leads-error">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    {t('fiLeads.loadFailed', 'This list could not be loaded. Refresh before assuming there are no leads.')}
                </p>
            ) : (
                <p className="text-xs text-muted-foreground">
                    {t('fiLeads.hint', 'Financing applications and warranty interest, so an F&I lead survives a missed text message. {{count}} still waiting.', { count: openCount })}
                </p>
            )}

            <div className="space-y-2">
                <p className="text-sm font-semibold">{t('fiLeads.financing', 'Financing applications')} ({apps.length})</p>
                {apps.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="cc-fi-leads-no-financing">{t('fiLeads.none', 'Nothing here yet.')}</p>
                ) : apps.map((l) => (
                    <Row key={l.id} lead={l} kind="vehicle-financing-applications" icon={Landmark}
                        title={l.full_name || t('fiLeads.aBuyer', 'A buyer')}
                        detail={[l.vehicle_label || l.stock_number, l.employment_status, l.income_range].filter(Boolean).join(' · ')} />
                ))}
            </div>

            <div className="space-y-2">
                <p className="text-sm font-semibold">{t('fiLeads.warranty', 'Warranty interest')} ({interest.length})</p>
                {interest.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="cc-fi-leads-no-warranty">{t('fiLeads.none', 'Nothing here yet.')}</p>
                ) : interest.map((l) => (
                    <Row key={l.id} lead={l} kind="vehicle-warranty-interest" icon={ShieldCheck}
                        title={l.name || t('fiLeads.aBuyer', 'A buyer')}
                        detail={[l.plan_label, l.stock_number].filter(Boolean).join(' · ')} />
                ))}
            </div>
        </div>
    );
};

export default CCFILeadsPanel;
